import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Instagram, Linkedin, Twitter } from 'lucide-react';
import { SwipeMatchLogo } from '../SwipeMatchLogo';
import { useLanguage } from '../../context/LanguageContext';

const Footer = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  // Scroll to top and navigate
  const handleLinkClick = (path) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(path);
  };

  const swipeLinks = language === 'nl' ? [
    { label: 'Start Swipen', path: '/swipematch' },
    { label: 'Hoe werkt het', path: '/hoe-werkt-het' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Premium', path: '/premium' }
  ] : [
    { label: 'Start Swiping', path: '/swipematch' },
    { label: 'How it works', path: '/hoe-werkt-het' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Premium', path: '/premium' }
  ];
    
  const forLandlordsLinks = language === 'nl' ? [
    { label: 'Gratis woning plaatsen', path: '/verhuurder/onboarding' },
    { label: 'Tarieven', path: '/premium' },
    { label: 'Tips verhuurders', path: '/tips-verhuurders' }
  ] : [
    { label: 'List property free', path: '/verhuurder/onboarding' },
    { label: 'Pricing', path: '/premium' },
    { label: 'Tips for landlords', path: '/tips-verhuurders' }
  ];
    
  const companyLinks = language === 'nl' ? [
    { label: 'Over ons', path: '/over-ons' },
    { label: 'Contact', path: '/contact' },
    { label: 'Blog', path: '/blog' },
    { label: 'Werken bij', path: '/carriere' }
  ] : [
    { label: 'About us', path: '/over-ons' },
    { label: 'Contact', path: '/contact' },
    { label: 'Blog', path: '/blog' },
    { label: 'Careers', path: '/carriere' }
  ];

  const legalLinks = language === 'nl' ? [
    { label: 'Privacy', path: '/privacy' },
    { label: 'Voorwaarden', path: '/voorwaarden' },
    { label: 'Veiligheid', path: '/veiligheid' }
  ] : [
    { label: 'Privacy', path: '/privacy' },
    { label: 'Terms', path: '/voorwaarden' },
    { label: 'Safety', path: '/veiligheid' }
  ];

  return (
    <footer className="bg-[#1F3A5F] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-2">
            <button onClick={() => handleLinkClick('/')} className="inline-block mb-4">
              <SwipeMatchLogo size="large" variant="white" />
            </button>
            <p className="text-white/60 mb-6 max-w-sm leading-relaxed">
              {language === 'nl' 
                ? 'De datingsite voor woningen. Swipe je weg naar je droomwoning in Nederland.'
                : 'The dating site for homes. Swipe your way to your dream home in the Netherlands.'
              }
            </p>
            <div className="flex gap-3">
              {[Instagram, Linkedin, Twitter].map((Icon, i) => (
                <a 
                  key={i} 
                  href="#" 
                  className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[#2E7D32] transition-colors"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Swipen */}
          <div>
            <h4 className="font-semibold text-white mb-4">
              {language === 'nl' ? 'Swipen' : 'Swipe'}
            </h4>
            <ul className="space-y-3">
              {swipeLinks.map((item) => (
                <li key={item.path}>
                  <button 
                    onClick={() => handleLinkClick(item.path)} 
                    className="text-white/60 hover:text-[#2E7D32] transition-colors text-left"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Voor Verhuurders */}
          <div>
            <h4 className="font-semibold text-white mb-4">
              {language === 'nl' ? 'Verhuurders' : 'Landlords'}
            </h4>
            <ul className="space-y-3">
              {forLandlordsLinks.map((item) => (
                <li key={item.path + item.label}>
                  <button 
                    onClick={() => handleLinkClick(item.path)} 
                    className="text-white/60 hover:text-[#2E7D32] transition-colors text-left"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Bedrijf */}
          <div>
            <h4 className="font-semibold text-white mb-4">
              {language === 'nl' ? 'Bedrijf' : 'Company'}
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((item) => (
                <li key={item.path}>
                  <button 
                    onClick={() => handleLinkClick(item.path)} 
                    className="text-white/60 hover:text-[#2E7D32] transition-colors text-left"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © 2026 Swipe je woning. {language === 'nl' ? 'Alle rechten voorbehouden.' : 'All rights reserved.'}
          </p>
          <div className="flex gap-6 text-sm">
            {legalLinks.map((item) => (
              <button 
                key={item.path}
                onClick={() => handleLinkClick(item.path)} 
                className="text-white/40 hover:text-white transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
